import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import inboundApi from '../../config/inboundApi';
import TicketHeader from './TicketHeader';
import ConversationThread from './ConversationThread';
import TicketPropertiesSidebar from './TicketPropertiesSidebar';
import ReplyComposer from './ReplyComposer';

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [ticketRes, staffRes] = await Promise.all([
          inboundApi.get(`/tickets/${ticketId}`),
          inboundApi.get('/staff?page_size=100').catch(() => ({ data: { staff: [] } })),
        ]);
        setTicket(ticketRes.data);
        setStaffList(staffRes.data.staff || []);

        try {
          const meRes = await inboundApi.get('/staff/me');
          setCurrentUserEmail(meRes.data.email || '');
        } catch {
          // fallback - not critical
        }
      } catch (err) {
        setError('Failed to load ticket');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticketId]);

  const handleSidebarUpdate = useCallback((data) => {
    setTicket((prev) => ({ ...prev, ...data }));
  }, []);

  const handleSubmitMessage = useCallback(async ({ type, body_html, body_text, status }) => {
    setSubmitting(true);
    try {
      const res = await inboundApi.post(`/tickets/${ticketId}/messages`, {
        type,
        body_html,
        body_text,
        status,
      });
      const newMsg = res.data.message;
      const updatedTicket = res.data.ticket;
      setTicket((prev) => ({
        ...prev,
        ...updatedTicket,
        messages: [...(prev.messages || []), newMsg],
      }));
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  }, [ticketId]);

  const onDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = sidebarWidth;

    const onMouseMove = (ev) => {
      if (!dragging.current) return;
      const delta = ev.clientX - startX.current;
      setSidebarWidth(Math.max(180, Math.min(500, startW.current + delta)));
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  if (loading) {
    return <div className="text-center py-5"><Spinner /></div>;
  }

  if (error && !ticket) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!ticket) {
    return <Alert variant="warning">Ticket not found</Alert>;
  }

  return (
    <div className="ticket-detail-layout">
      <div style={{ width: sidebarWidth, minWidth: sidebarWidth, flexShrink: 0 }}>
        <TicketPropertiesSidebar
          ticket={ticket}
          staffList={staffList}
          currentUserEmail={currentUserEmail}
          onUpdate={handleSidebarUpdate}
        />
      </div>
      <div className="sidebar-divider-handle" onMouseDown={onDividerMouseDown} />
      <div className="ticket-main">
        <TicketHeader
          ticket={ticket}
          onBack={() => navigate('/tickets')}
        />
        <ConversationThread messages={ticket.messages} />
        {error && (
          <Alert variant="danger" className="mx-3 mb-0" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <ReplyComposer
          onSubmit={handleSubmitMessage}
          submitting={submitting}
        />
      </div>
    </div>
  );
}
